import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { MetricsVideo } from '@/services/dynamicFlowsService';
import { formatNumber, formatPercent, formatSeconds } from './format';

// Números dos eixos (tempo e %) menores que o padrão do Recharts.
const AXIS_TICK = { fontSize: 11, fill: '#6B7280' };

interface TooltipPayload {
  active?: boolean;
  payload?: { payload: { seconds: number; percent: number } }[];
}

function RetentionTip({ active, payload }: TooltipPayload) {
  useLanguage();
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-[#D9E0EA] bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-semibold text-[#111827]">{formatSeconds(point.seconds)}</p>
      <p className="text-[#4B5563]">
        {point.percent.toLocaleString(getInterfaceLanguage(), { maximumFractionDigits: 2 })}{translateInterfaceText("% ainda assistindo")}</p>
    </div>
  );
}

// Curva de retenção no mesmo formato da tela de Métricas da plataforma (linha, 0–100%).
export function VideoRetentionChart({ video }: { video: MetricsVideo }) {
  useLanguage();
  const hasData = video.retention.length > 0;

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-[#D9E0EA] bg-white p-[18px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[15px] font-bold text-[#111827]">{translateInterfaceText("Retenção:")}{" "}{video.name}</h2>
        {hasData && (
          <p className="text-xs text-[#4B5563]">
            {translateInterfaceText("Duração")}{" "}{formatSeconds(video.duration)} {" "}{translateInterfaceText("· assistido em média")}{" "}{formatPercent(video.avg_watched)} (
            {formatSeconds(video.avg_watched_seconds)}) · {formatNumber(video.plays)} {" "}{translateInterfaceText("plays analisados")}</p>
        )}
      </div>
      {hasData ? (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={video.retention} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="seconds"
                type="number"
                domain={[0, 'dataMax']}
                tickFormatter={formatSeconds}
                tick={AXIS_TICK}
                label={{ value: translateInterfaceText("Tempo do vídeo"), position: 'insideBottom', offset: -8, fontSize: 12, fill: '#4B5563' }}
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(value: number) => `${value}%`}
                tick={AXIS_TICK}
                width={40}
              />
              <Tooltip content={<RetentionTip />} />
              <Line
                type="monotone"
                dataKey="percent"
                stroke="#0057FF"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#0057FF' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-[#4B5563]">{translateInterfaceText("Ainda não há plays deste vídeo no período.")}</p>
      )}
    </section>
  );
}
